import {
  BriefCampaignInf,
  ICampaignDeliverableTask,
} from "@shared/lib/types/briefCampains";
import { IBriefDeliverableConfig } from "@shared/lib/types/briefConfig";
import {
  CampaignTimelineStage,
  DeliverableStatus,
  GanttCreator,
  GanttDeliverable,
} from "@widgets/GanttChart";

export function toISOStringSafe(dateStr?: string): string | null {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return `${dateStr}T00:00:00.000Z`;
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export function mapTaskToDeliverableStatus(
  task?: ICampaignDeliverableTask,
  isCancelled?: boolean
): DeliverableStatus {
  if (isCancelled) return "cancelled";
  if (!task) return "script_pending";
  const type = (task.type || "").toLowerCase();
  const status = (task.status || "").toLowerCase();

  const isTaskCancelled =
    status.includes("cancel") ||
    status.includes("decline") ||
    status.includes("withdraw");
  if (isTaskCancelled) {
    return "cancelled";
  }

  const isApprovedOrCompleted =
    (status.includes("complete") || status.includes("approved") || status === "approved") &&
    !status.includes("pending");

  const isChangesRequested =
    status.includes("edit") ||
    status.includes("change");

  if (type.includes("live")) {
    if (isApprovedOrCompleted) {
      return "live_completed";
    }
    return "live_pending";
  }

  if (type.includes("draft")) {
    if (isApprovedOrCompleted) {
      return "live_pending";
    }
    if (isChangesRequested) {
      return "draft_changes";
    }
    if (status.includes("approval") || status.includes("pending-approval")) {
      return "draft_approval";
    }
    if (status.includes("uploaded")) {
      return "draft_internal_review";
    }
    return "draft_pending";
  }

  if (type.includes("script")) {
    if (isApprovedOrCompleted) {
      return "draft_pending";
    }
    if (isChangesRequested) {
      return "script_changes";
    }
    if (status.includes("approval") || status.includes("pending-approval")) {
      return "script_approval";
    }
    if (status.includes("uploaded")) {
      return "script_internal_review";
    }
    return "script_pending";
  }

  return "script_pending";
}

const STAGE_COLUMNS = [
  { key: "brief", label: "Brief" },
  { key: "script", label: "Script" },
  { key: "draft", label: "Draft" },
  { key: "live", label: "Live" },
];

export function findTaskForStage(
  tasks: Array<{ taskId?: string; name?: string; type?: string }> | undefined,
  stageKey: string,
  stageLabel: string
) {
  if (!tasks || tasks.length === 0) return undefined;
  const sKey = stageKey.toLowerCase();
  const sLabel = stageLabel.toLowerCase();
  return tasks.find((t) => {
    if (t.type && t.type.toLowerCase() === sKey) return true;
    if (
      t.name &&
      (t.name.toLowerCase().includes(sKey) ||
        t.name.toLowerCase().includes(sLabel))
    ) {
      return true;
    }
    return false;
  });
}

export function adaptCampaignInfluencersToGantt(
  influencers: BriefCampaignInf[],
  config?: IBriefDeliverableConfig[]
): GanttCreator[] {
  if (!influencers || influencers.length === 0) {
    return [];
  }

  return influencers.map((inf) => {
    const infRecord = inf as unknown as Record<string, unknown>;
    const infRawStatus =
      typeof infRecord.status === "string" ? infRecord.status.toLowerCase() : "";
    const isInfCancelled = Boolean(
      inf.isBrandDeclined ||
      inf.isCreatorDeclined ||
      inf.creatorDeclinedReason ||
      inf.brandDeclinedReason ||
      infRawStatus.includes("cancel") ||
      infRawStatus.includes("decline") ||
      infRawStatus.includes("withdraw")
    );

    const name =
      inf.influencerName ||
      inf.socialMedia?.searchV1?.fullName ||
      `${inf.influencer?.firstName || ""} ${inf.influencer?.lastName || ""}`.trim() ||
      inf.socialMedia?.username ||
      "Creator";
    const handle = inf.socialMedia?.username
      ? `@${inf.socialMedia.username}`
      : "";
    const image =
      inf.socialMedia?.profilePicUrl;

    const deliverables: GanttDeliverable[] = [];

    if (inf.deliverables && inf.deliverables.length > 0) {
      inf.deliverables.forEach((del, idx) => {
        const delRecord = del as unknown as Record<string, unknown>;
        const delRawStatus =
          typeof delRecord.status === "string" ? delRecord.status.toLowerCase() : "";
        const isDelCancelled = Boolean(
          isInfCancelled ||
          Boolean(delRecord.isCancelled) ||
          delRawStatus.includes("cancel") ||
          delRawStatus.includes("decline") ||
          delRawStatus.includes("withdraw")
        );

        const activeTask =
          del.lastestTask ||
          del.tasks?.[del.tasks.length - 1] ||
          del.tasks?.[0];

        const buildconfig = config?.find(
          (c) =>
            c.deliverableConfigRefId === del._id
        );

        const configuredTasks =
          buildconfig?.tasks && buildconfig.tasks.length > 0
            ? buildconfig.tasks
            : (del.tasks || []).map((t) => ({
                taskId: t._id,
                name: t.taskName || t.type || "Task",
                type: t.type,
              }));

        const stages: CampaignTimelineStage[] = [];

        if (configuredTasks.length > 0) {
          // Add matching standard stages in STAGE_COLUMNS order
          STAGE_COLUMNS.forEach((stage) => {
            const taskConfig = findTaskForStage(
              configuredTasks,
              stage.key,
              stage.label
            );
            if (!taskConfig) return;

            const currentTask = del.tasks?.find(
              (t) =>
                (taskConfig.taskId && t._id === taskConfig.taskId) ||
                (t.type && t.type.toLowerCase() === stage.key.toLowerCase()) ||
                (t.taskName &&
                  (t.taskName.toLowerCase().includes(stage.key.toLowerCase()) ||
                    t.taskName.toLowerCase().includes(stage.label.toLowerCase())))
            );

            const stageId = stage.key;
            const label = currentTask?.taskName || taskConfig.name || stage.label;
            const startDate = currentTask?.startAt
              ? currentTask.startAt.split("T")[0]
              : undefined;
            const endDate = currentTask?.endAt
              ? currentTask.endAt.split("T")[0]
              : undefined;

            const isTaskCancelled = Boolean(
              isDelCancelled ||
              currentTask?.status?.toLowerCase().includes("cancel") ||
              currentTask?.status?.toLowerCase().includes("decline") ||
              currentTask?.status?.toLowerCase().includes("withdraw")
            );

            stages.push({
              id: stageId,
              taskId: currentTask?._id || taskConfig.taskId,
              status: isTaskCancelled ? "cancelled" : currentTask?.status,
              label,
              enabled: true,
              startDate,
              endDate,
              isCancelled: isTaskCancelled,
            });
          });

          // Also append any custom tasks in configuredTasks that were not standard stages
          configuredTasks.forEach((cfgTask) => {
            const alreadyAdded = stages.some(
              (s) =>
                (cfgTask.taskId && s.taskId === cfgTask.taskId) ||
                (cfgTask.type && s.id === cfgTask.type.toLowerCase())
            );
            if (!alreadyAdded) {
              const currentTask = del.tasks?.find(
                (t) =>
                  (cfgTask.taskId && t._id === cfgTask.taskId) ||
                  (cfgTask.type &&
                    t.type?.toLowerCase() === cfgTask.type.toLowerCase()) ||
                  (cfgTask.name &&
                    t.taskName?.toLowerCase() === cfgTask.name.toLowerCase())
              );
              const stageId = (
                cfgTask.type ||
                cfgTask.name ||
                cfgTask.taskId ||
                ""
              ).toLowerCase();
              const label =
                currentTask?.taskName || cfgTask.name || stageId.toUpperCase();
              const startDate = currentTask?.startAt
                ? currentTask.startAt.split("T")[0]
                : undefined;
              const endDate = currentTask?.endAt
                ? currentTask.endAt.split("T")[0]
                : undefined;
              const isTaskCancelled = Boolean(
                isDelCancelled ||
                currentTask?.status?.toLowerCase().includes("cancel") ||
                currentTask?.status?.toLowerCase().includes("decline") ||
                currentTask?.status?.toLowerCase().includes("withdraw")
              );
              stages.push({
                id: stageId,
                taskId: currentTask?._id || cfgTask.taskId,
                status: isTaskCancelled
                  ? "cancelled"
                  : currentTask?.status,
                label,
                enabled: true,
                startDate,
                endDate,
                isCancelled: isTaskCancelled,
              });
            }
          });
        }

        deliverables.push({
          id: del._id || `${inf._id}-del-${idx}`,
          name: del.deliverableType || `Deliverable ${idx + 1}`,
          status: mapTaskToDeliverableStatus(activeTask, isDelCancelled),
          stages,
          isCancelled: isDelCancelled,
        });
      });
    }

    return {
      id: inf._id,
      name,
      influencerName: inf.influencerName || name,
      handle,
      platform: inf.socialMedia?.platform || "instagram",
      image,
      deliverables,
      isCancelled: isInfCancelled,
    };
  });
}
